import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, ViewStyle } from 'react-native';
import { colors } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

export interface PulsingAccentBarProps {
  /** When false, renders a plain static bar — zero animation overhead and
   * zero visual change for sections with nothing actionable pending. */
  pulsing?: boolean;
  style?: ViewStyle;
}

/** Drop-in accent bar that pulses opacity when something needs attention
 * (ongoing flows, overdue assignments). Modeled on TypingIndicator.tsx's
 * existing Animated.loop opacity-oscillation pattern — the only prior art
 * for this kind of idle-state animation in the codebase. */
export const PulsingAccentBar: React.FC<PulsingAccentBarProps> = ({ pulsing = false, style }) => {
  const styles = useThemedStyles(getStyles);
  const opacity = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    if (pulsing) {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.timing(opacity, { toValue: 0.5, duration: 900, useNativeDriver: true }),
          Animated.timing(opacity, { toValue: 1, duration: 900, useNativeDriver: true }),
        ])
      );
      loop.start();
      return () => loop.stop();
    }
    opacity.setValue(1);
  }, [pulsing, opacity]);

  return <Animated.View style={[styles.bar, style, { opacity }]} />;
};

const getStyles = () => StyleSheet.create({
  bar: {
    width: 3,
    height: 16,
    borderRadius: 2,
    backgroundColor: colors.brand.primary,
  },
});
